// UserPortalAuditLogs.jsx
//
// User-role version of the admin Portal Audit Log page. Shows only the
// logged-in user's own actions (server-side `WHERE user_id = JWT.identity`
// in /api/userportalauditlog). No User column, no username filter, no
// clear-logs capability.
//
// Mirrors the visual + behavior of components/admin/AdminPortalAuditLogs.jsx
// where it makes sense (column shapes, diff dialog, CSV export pattern).

import React, { useEffect, useMemo, useState } from "react";
import "../../../src/style.css";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ThemeProvider,
  Tooltip,
  Typography,
  createTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import moment from "moment";
import Datetime from "react-datetime";
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
} from "@mui/x-data-grid";
import { useDispatch, useSelector } from "react-redux";
import { getUserPortalAuditLogs } from "../../redux/actions/userPortalAuditLogs";
import { StyledDataGrid } from "../../pages/CustomDataGrid";
import PageTitle from "../../components/common/PageTitle";

const MuiTheme = createTheme({
  components: {
    MuiDataGrid: {
      defaultProps: { density: "compact" },
    },
  },
});

function CustomToolbar() {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarDensitySelector />
      <GridToolbarFilterButton />
    </GridToolbarContainer>
  );
}

const ACTION_COLORS = {
  CREATED: "success",
  UPDATED: "info",
  DELETED: "error",
  SUSPENDED: "warning",
  ACTIVATED: "success",
  LOGIN_SUCCESS: "success",
  LOGIN_FAILED: "error",
  ACCOUNT_LOCKED: "error",
  PASSWORD_CHANGED: "warning",
  PASSWORD_RESET_REQUESTED: "info",
  PASSWORD_RESET_COMPLETED: "warning",
  PASSWORD_RESET_FAILED: "error",
  HANGUP: "warning",
};

// Entity types and actions a regular user can realistically generate.
// (Admin-only entities like CARRIER/ACL/ROLE_PERM/INVOICE/BILLING/PRODUCT/
// PROMOTIONAL are intentionally omitted — they'd produce empty results.)
// AUTH (login attempts) is excluded entirely — see backend SQL filter; the
// admin "User Login" button writes a LOGIN_SUCCESS row indistinguishable
// from the user's own login, so we hide the whole category for now.
const ENTITY_TYPES = [
  "ALL",
  "DID",
  "CAMPAIGN",
  "BUYER",
  "EXTENSION",
  "CALL_BLOCK",
  "QUEUE",
  "QUEUE_MEMBER",
  "RECORDING",
  "MOH",
  "KYC_DOC",
  "USER",
];

const ACTIONS = [
  "ALL",
  "CREATED",
  "UPDATED",
  "DELETED",
  "PASSWORD_CHANGED",
  "PASSWORD_RESET_REQUESTED",
  "PASSWORD_RESET_COMPLETED",
  "PASSWORD_RESET_FAILED",
  "HANGUP",
];

function UserPortalAuditLogs({ userThem }) {
  const dispatch = useDispatch();
  const { portalAuditLogs, loading } = useSelector(
    (state) => state.getUserPortalAuditLogs || { portalAuditLogs: [], loading: false },
  );

  const [fromDate, setFromDate] = useState(moment().startOf("day"));
  const [toDate, setToDate] = useState(moment().endOf("day"));
  const [entityType, setEntityType] = useState("ALL");
  const [action, setAction] = useState("ALL");
  const [diffDialogOpen, setDiffDialogOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const handleSearch = () => {
    const data = {
      from_date: moment(fromDate).format("YYYY-MM-DD HH:mm"),
      to_date: moment(toDate).format("YYYY-MM-DD HH:mm"),
    };
    if (entityType !== "ALL") data.entity_type = entityType;
    if (action !== "ALL") data.action = action;
    dispatch(getUserPortalAuditLogs(data));
  };

  const handleReset = () => {
    setFromDate(moment().startOf("day"));
    setToDate(moment().endOf("day"));
    setEntityType("ALL");
    setAction("ALL");
    dispatch(
      getUserPortalAuditLogs({
        from_date: moment().startOf("day").format("YYYY-MM-DD HH:mm"),
        to_date: moment().endOf("day").format("YYYY-MM-DD HH:mm"),
      }),
    );
  };

  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = useMemo(() => {
    if (!portalAuditLogs || !Array.isArray(portalAuditLogs)) return [];
    return portalAuditLogs.map((log, index) => ({ id: index + 1, ...log }));
  }, [portalAuditLogs]);

  const handleRowClick = (params) => {
    const row = params.row;
    if (row.before_state || row.after_state) {
      setSelectedRow(row);
      setDiffDialogOpen(true);
    }
  };

  const columns = [
    {
      field: "created_at",
      headerName: "Date/Time",
      width: 170,
      renderCell: (params) => {
        if (!params.value) return "";
        const m = moment(params.value);
        return (
          <span>
            <span style={{ color: "#1565c0" }}>{m.format("DD/MM/YYYY")}</span>{" "}
            <span style={{ color: "green" }}>{m.format("HH:mm:ss")}</span>
          </span>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      width: 150,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={ACTION_COLORS[params.value] || "default"}
          variant="outlined"
        />
      ),
    },
    {
      field: "entity_type",
      headerName: "Entity",
      width: 130,
      renderCell: (params) => (
        <Chip label={params.value} size="small" variant="filled" />
      ),
    },
    {
      field: "entity_id",
      headerName: "Entity ID",
      width: 140,
      renderCell: (params) => (
        <span style={{ color: "#1565c0" }}>{params.value}</span>
      ),
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      minWidth: 250,
    },
    {
      field: "changed_fields",
      headerName: "Changes",
      width: 110,
      renderCell: (params) => {
        const fields = params.value;
        if (!fields || fields.length === 0) return "---";
        return (
          <Chip
            label={`${fields.length} field(s)`}
            size="small"
            color="primary"
            variant="outlined"
            style={{ cursor: "pointer" }}
          />
        );
      },
    },
    {
      field: "source_ip",
      headerName: "IP",
      width: 140,
      renderCell: (params) => (
        <span style={{ color: "#1565c0" }}>{params.value}</span>
      ),
    },
  ];

  const handleExportCSV = () => {
    if (!rows || rows.length === 0) return;

    const escape = (val) => {
      if (val === null || val === undefined) return "";
      let s = typeof val === "string" ? val : JSON.stringify(val);
      if (/[",\n\r]/.test(s)) {
        s = `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };

    const formatDate = (dateStr) => {
      if (!dateStr) return "";
      const m = moment(dateStr);
      return m.isValid() ? m.format("DD/MM/YYYY HH:mm:ss") : "";
    };

    const headers = [
      "Date/Time",
      "Action",
      "Entity Type",
      "Entity ID",
      "Description",
      "Changed Fields",
      "Before State",
      "After State",
      "Source IP",
    ];

    const lines = rows.map((r) =>
      [
        formatDate(r.created_at),
        r.action || "",
        r.entity_type || "",
        r.entity_id || "",
        r.description || "",
        Array.isArray(r.changed_fields)
          ? r.changed_fields.join("; ")
          : r.changed_fields || "",
        r.before_state
          ? typeof r.before_state === "string"
            ? r.before_state
            : JSON.stringify(r.before_state)
          : "",
        r.after_state
          ? typeof r.after_state === "string"
            ? r.after_state
            : JSON.stringify(r.after_state)
          : "",
        r.source_ip || "",
      ]
        .map(escape)
        .join(","),
    );

    const csvWithBOM = "﻿" + headers.join(",") + "\n" + lines.join("\n");
    const blob = new Blob([csvWithBOM], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const stamp = moment().format("YYYYMMDD_HHmmss");
    a.download = `user_activity_log_${stamp}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const parseDiffData = (row) => {
    if (!row) return [];
    let before = {};
    let after = {};
    try {
      before =
        typeof row.before_state === "string"
          ? JSON.parse(row.before_state)
          : row.before_state || {};
      after =
        typeof row.after_state === "string"
          ? JSON.parse(row.after_state)
          : row.after_state || {};
    } catch (e) {
      return [];
    }
    const allKeys = [
      ...new Set([...Object.keys(before), ...Object.keys(after)]),
    ];
    return allKeys.map((key) => ({
      field: key,
      before: before[key] !== undefined ? String(before[key]) : "---",
      after: after[key] !== undefined ? String(after[key]) : "---",
    }));
  };

  return (
    <>
      <div className={`App ${userThem} `}>
        <div className="contant_box">
          <div className="main">
            <section className="sidebar-sec">
              <div className="container-fluid">
                <div className="row">
                  <div className="col-lg-12">
                    <div className="">
                      <div className="tab-content" id="pills-tabContent">
                        <div
                          className="tab-pane fade show active"
                          id="pills-home"
                          role="tabpanel"
                          aria-labelledby="pills-home-tab"
                        >
                          <div className="tab_cntnt_box">
                            <div className="cntnt_title">
                              <PageTitle title="Activity Log" />
                            </div>

                            {/* Filters row */}
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "flex-end",
                                alignItems: "end",
                                gap: 2,
                                flexWrap: "wrap",
                                mb: 2,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "end",
                                  gap: 2,
                                  flexWrap: "wrap",
                                }}
                              >
                                <Box>
                                  <Typography
                                    sx={{ fontSize: 14, fontWeight: 500 }}
                                  >
                                    From Date:
                                  </Typography>
                                  <Datetime
                                    className="datefield_select frm_date m-0 new_input"
                                    value={fromDate}
                                    onChange={(val) => setFromDate(val)}
                                    dateFormat="DD/MM/YYYY"
                                    timeFormat="HH:mm"
                                  />
                                </Box>
                                <Box>
                                  <Typography
                                    sx={{ fontSize: 14, fontWeight: 500 }}
                                  >
                                    To Date:
                                  </Typography>
                                  <Datetime
                                    className="datefield_select new_input m-0"
                                    value={toDate}
                                    onChange={(val) => setToDate(val)}
                                    dateFormat="DD/MM/YYYY"
                                    timeFormat="HH:mm"
                                  />
                                </Box>
                                <Select
                                  size="small"
                                  value={entityType}
                                  onChange={(e) => setEntityType(e.target.value)}
                                  sx={{ minWidth: 140 }}
                                >
                                  {ENTITY_TYPES.map((t) => (
                                    <MenuItem key={t} value={t}>
                                      {t}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <Select
                                  size="small"
                                  value={action}
                                  onChange={(e) => setAction(e.target.value)}
                                  sx={{ minWidth: 160 }}
                                >
                                  {ACTIONS.map((a) => (
                                    <MenuItem key={a} value={a}>
                                      {a}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <Box sx={{ display: "flex" }}>
                                  <IconButton
                                    className="filter_search_btn"
                                    style={{ marginLeft: "0 !important" }}
                                    onClick={handleSearch}
                                  >
                                    Search&nbsp;
                                    <SearchIcon />
                                  </IconButton>
                                  <IconButton
                                    className="filter_reset_btn"
                                    style={{
                                      marginLeft: "0 !important",
                                      backgroundColor: "black",
                                    }}
                                    onClick={handleReset}
                                  >
                                    Reset&nbsp;
                                    <RestartAltIcon />
                                  </IconButton>
                                  <Tooltip
                                    title={
                                      rows.length === 0
                                        ? "No data to export"
                                        : `Export ${rows.length} row(s) to CSV`
                                    }
                                    arrow
                                  >
                                    <span>
                                      <IconButton
                                        className="filter_search_btn"
                                        style={{ marginLeft: "0 !important" }}
                                        onClick={handleExportCSV}
                                        disabled={rows.length === 0}
                                      >
                                        Export&nbsp;
                                        <FileDownloadIcon />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                </Box>
                              </Box>
                            </Box>

                            {/* Data Grid */}
                            <ThemeProvider theme={MuiTheme}>
                              <StyledDataGrid
                                className="custom_header_redirect"
                                rows={rows}
                                columns={columns}
                                loading={loading}
                                autoHeight
                                density="compact"
                                onRowClick={handleRowClick}
                                components={{ Toolbar: CustomToolbar }}
                                sx={{
                                  cursor: "pointer",
                                  // Lock header bg + text color in the component
                                  // itself. style.css selectors are hash-suffixed
                                  // and can drift on MUI rebuilds — when they
                                  // miss, headers render dark-on-dark (theme_blue)
                                  // or white-on-white (theme_white) and become
                                  // invisible. Same guard as AdminPortalAuditLogs.
                                  "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader": {
                                    backgroundColor: "#0a140e !important",
                                  },
                                  "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeaderTitle, & .MuiDataGrid-columnHeaderTitleContainer, & .MuiDataGrid-sortIcon, & .MuiDataGrid-menuIconButton": {
                                    color: "#ffffff !important",
                                    fontWeight: "bold",
                                  },
                                  "& .MuiDataGrid-toolbarContainer": {
                                    gap: "1px",
                                  },
                                  "& .MuiButton-root": {
                                    fontSize: "calc(0.6rem + 0.4vw)",
                                    minWidth: "unset",
                                  },
                                  "& .MuiSvgIcon-root": {
                                    fontSize: "calc(0.6rem + 0.4vw)",
                                  },
                                }}
                              />
                            </ThemeProvider>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Before/After Diff Dialog */}
      <Dialog
        open={diffDialogOpen}
        onClose={() => setDiffDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            color: "#212121 !important",
          }}
        >
          <span>
            Change Details{" "}
            {selectedRow && (
              <>
                <Chip
                  label={selectedRow.action}
                  size="small"
                  color={ACTION_COLORS[selectedRow.action] || "default"}
                  sx={{ ml: 1 }}
                />
                <Chip
                  label={selectedRow.entity_type}
                  size="small"
                  sx={{ ml: 1 }}
                />
              </>
            )}
          </span>
          <IconButton onClick={() => setDiffDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ "& *": { color: "#212121 !important" } }}>
          {selectedRow && (
            <>
              <Typography
                variant="body2"
                sx={{ mb: 2, color: "gray !important" }}
              >
                {selectedRow.description}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{ fontWeight: "bold", color: "#212121 !important" }}
                    >
                      Field
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: "bold", color: "#d32f2f !important" }}
                    >
                      Before
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: "bold", color: "#2e7d32 !important" }}
                    >
                      After
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parseDiffData(selectedRow).map((row) => (
                    <TableRow key={row.field}>
                      <TableCell
                        sx={{ fontWeight: 500, color: "#212121 !important" }}
                      >
                        {row.field}
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#ffebee",
                          color: "#d32f2f !important",
                        }}
                      >
                        {row.before}
                      </TableCell>
                      <TableCell
                        sx={{
                          backgroundColor: "#e8f5e9",
                          color: "#2e7d32 !important",
                        }}
                      >
                        {row.after}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDiffDialogOpen(false)}
            sx={{ textTransform: "none" }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default UserPortalAuditLogs;
